/**
 * @file ScrollbarBookmarks.jsx
 * @description Custom scrollbar overlay for the chat main area.
 * Renders a semi-transparent position thumb and solid bookmark markers
 * at the location of each user message in the conversation.
 *
 * - Clicking the track scrolls proportionally.
 * - Clicking a bookmark smooth-scrolls to that message.
 * - Hovering a bookmark shows a preview snippet of the message.
 * - Hidden entirely when content fits without scrolling.
 *
 * Bookmark density algorithm (3 phases):
 *   1. Plenty of space  → full height (BOOKMARK_MAX_H), greedy-push for ideal positions
 *   2. Getting crowded  → shrink height proportionally so all N bookmarks still fit
 *   3. Still too dense  → lock at min height (BOOKMARK_MIN_H) and cull to an
 *                         evenly-distributed subset so the overview is preserved
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import styles from '../../styles/ScrollbarBookmarks.module.css';

// ─── Layout constants ───────────────────────────────────────────────────────
// BOOKMARK_MAX_H must match the CSS `.bookmark { height }` fallback, but since
// height is driven entirely through inline style now, it is purely a JS value.
const BOOKMARK_MAX_H = 18; // px — full-size height
const BOOKMARK_MIN_H = 5;  // px — minimum before culling kicks in
const BOOKMARK_GAP   = 1;  // px — minimum clear space between adjacent bookmarks
// ────────────────────────────────────────────────────────────────────────────

/**
 * Returns `count` indices evenly distributed across [0, total-1].
 * Always includes index 0 (oldest) and total-1 (newest) when count >= 2,
 * preserving the spatial overview of the full conversation.
 */
function distributeIndices(total, count) {
    if (count <= 0) return [];
    if (count >= total) return Array.from({ length: total }, (_, i) => i);
    if (count === 1) return [Math.floor((total - 1) / 2)]; // pick middle message
    const indices = [];
    for (let i = 0; i < count; i++) {
        indices.push(Math.round(i * (total - 1) / (count - 1)));
    }
    return [...new Set(indices)]; // deduplicate any rounding collisions
}

export default function ScrollbarBookmarks({ scrollContainerRef, messages }) {
    const [thumbState, setThumbState] = useState({ top: 0, height: 30, visible: false });
    const [bookmarks, setBookmarks]   = useState([]);
    const trackRef = useRef(null);

    // ── Thumb: updates on every scroll event (no DOM queries, just math) ────
    const updateThumb = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;

        if (scrollHeight <= clientHeight) {
            setThumbState(s => ({ ...s, visible: false }));
            return;
        }

        // Standard formula: thumb height = viewport fraction of total content
        const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * clientHeight);
        const maxTop      = clientHeight - thumbHeight;
        const top         = (scrollTop / (scrollHeight - clientHeight)) * maxTop;

        setThumbState({ top, height: thumbHeight, visible: true });
    }, [scrollContainerRef]);

    // ── Bookmarks: rebuilt only when messages change or the container resizes ─
    const updateBookmarks = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const { scrollHeight, clientHeight } = container;
        if (scrollHeight <= clientHeight) {
            setBookmarks([]);
            return;
        }

        const allEls = Array.from(container.querySelectorAll('[data-user-msg]'));
        if (allEls.length === 0) {
            setBookmarks([]);
            return;
        }

        const N           = allEls.length;
        const trackHeight = clientHeight; // track spans the same height as scroll viewport
        const spaceEach   = trackHeight / N;

        // ── Phase 1 & 2: determine active height and which elements to render ──
        let activeHeight;
        let els;

        if (spaceEach >= BOOKMARK_MAX_H + BOOKMARK_GAP) {
            // Plenty of space — render all at full height
            activeHeight = BOOKMARK_MAX_H;
            els = allEls;

        } else if (spaceEach >= BOOKMARK_MIN_H + BOOKMARK_GAP) {
            // Getting crowded — shrink uniformly so all N still fit without culling
            activeHeight = spaceEach - BOOKMARK_GAP;
            els = allEls;

        } else {
            // Too dense even at minimum height — cull to an evenly-distributed subset
            activeHeight        = BOOKMARK_MIN_H;
            const maxFit        = Math.floor(trackHeight / (BOOKMARK_MIN_H + BOOKMARK_GAP));
            const keptIndices   = distributeIndices(N, maxFit);
            els = keptIndices.map(i => allEls[i]);
        }

        // ── Phase 3: ideal positions → greedy downward push → clamp ───────────
        // Center each bookmark on the message's proportional position in the content.
        const idealTops = els.map(el =>
            (el.offsetTop / scrollHeight) * trackHeight - activeHeight / 2
        );

        const adjustedTops = [];
        let prevBottom = 0;
        for (let i = 0; i < idealTops.length; i++) {
            const top = Math.min(
                Math.max(idealTops[i], prevBottom),  // push down if overlapping previous
                trackHeight - activeHeight            // clamp to bottom of track
            );
            adjustedTops.push(top);
            prevBottom = top + activeHeight + BOOKMARK_GAP;
        }

        setBookmarks(
            els.map((el, i) => ({
                top:     adjustedTops[i], // final top edge in px — no CSS transform applied
                height:  activeHeight,    // dynamic; passed as inline style
                preview: el.dataset.preview || '',
                element: el,
            }))
        );
    }, [scrollContainerRef]);

    // Attach scroll listener for the thumb
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.addEventListener('scroll', updateThumb, { passive: true });
        updateThumb();

        return () => container.removeEventListener('scroll', updateThumb);
    }, [scrollContainerRef, updateThumb]);

    // Recalculate bookmarks + thumb when messages change or container resizes
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        // rAF ensures the DOM has painted new messages before we measure offsetTops
        const frame = requestAnimationFrame(() => {
            updateThumb();
            updateBookmarks();
        });

        const ro = new ResizeObserver(() => {
            updateThumb();
            updateBookmarks();
        });
        ro.observe(container);

        return () => {
            cancelAnimationFrame(frame);
            ro.disconnect();
        };
    }, [messages, scrollContainerRef, updateThumb, updateBookmarks]);

    // Click on track → scroll proportionally to click position
    const handleTrackClick = useCallback((e) => {
        if (e.target.closest('[data-bookmark]')) return; // bookmarks handle their own click
        if (e.target.closest('[data-thumb]'))   return; // thumb drag handles its own events
        const container = scrollContainerRef.current;
        const track     = trackRef.current;
        if (!container || !track) return;

        const rect     = track.getBoundingClientRect();
        const fraction = (e.clientY - rect.top) / rect.height;
        container.scrollTop = fraction * (container.scrollHeight - container.clientHeight);
    }, [scrollContainerRef]);

    // Click on bookmark → smooth-scroll to that message
    const handleBookmarkClick = useCallback((e, element) => {
        e.stopPropagation();
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    // Drag the thumb → scroll continuously while the pointer is held
    const handleThumbMouseDown = useCallback((e) => {
        e.preventDefault();    // prevent text selection while dragging
        e.stopPropagation();   // prevent the track click from also firing

        const container = scrollContainerRef.current;
        const track     = trackRef.current;
        if (!container || !track) return;

        const startY         = e.clientY;
        const startScrollTop = container.scrollTop;

        // Show a grabbing cursor globally so it persists when the pointer leaves the track
        document.body.style.cursor    = 'grabbing';
        document.body.style.userSelect = 'none';

        const onMouseMove = (moveEvent) => {
            const trackHeight = track.getBoundingClientRect().height;
            const { scrollHeight, clientHeight } = container;

            // Recompute thumb height fresh (avoids stale closure value)
            const thumbH    = Math.max(30, (clientHeight / scrollHeight) * clientHeight);
            const thumbRange  = trackHeight - thumbH;
            if (thumbRange <= 0) return;

            const scrollRange  = scrollHeight - clientHeight;
            const deltaY       = moveEvent.clientY - startY;
            const newScrollTop = startScrollTop + (deltaY / thumbRange) * scrollRange;
            container.scrollTop = Math.max(0, Math.min(newScrollTop, scrollRange));
        };

        const onMouseUp = () => {
            document.body.style.cursor     = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup',   onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup',   onMouseUp);
    }, [scrollContainerRef]);

    if (!thumbState.visible) return null;

    return (
        <div className={styles.track} ref={trackRef} onClick={handleTrackClick}>

            {/* Semi-transparent thumb — shows current scroll position; draggable */}
            <div
                className={styles.thumb}
                style={{ top: `${thumbState.top}px`, height: `${thumbState.height}px` }}
                data-thumb="true"
                onMouseDown={handleThumbMouseDown}
            />

            {/* Bookmarks — height is dynamic (set by the density algorithm above) */}
            {bookmarks.map((bm, i) => (
                <div
                    key={i}
                    className={styles.bookmark}
                    style={{ top: `${bm.top}px`, height: `${bm.height}px` }}
                    data-bookmark="true"
                    onClick={(e) => handleBookmarkClick(e, bm.element)}
                >
                    <div className={styles.tooltip}>{bm.preview}</div>
                </div>
            ))}
        </div>
    );
}